class SmbBrandMarquee extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.viewport = this.querySelector('[data-marquee-viewport]');
    this.track = this.querySelector('[data-marquee-track]');
    this.groups = this.querySelectorAll('.smb-marquee__group');
    this.prevBtn = this.querySelector('[data-marquee-prev]');
    this.nextBtn = this.querySelector('[data-marquee-next]');
    this.playPauseBtn = this.querySelector('[data-marquee-playpause]');

    if (!this.track || !this.viewport || this.groups.length < 2) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.reducedMotion) {
      if (this.prevBtn) this.prevBtn.hidden = true;
      if (this.nextBtn) this.nextBtn.hidden = true;
      if (this.playPauseBtn) this.playPauseBtn.hidden = true;
      return;
    }

    this.position = 0;
    this.groupWidth = 1;
    this.autoPaused = false;
    this.manualPaused = false;
    this.isDragging = false;
    this.pointerId = null;
    this.dragStartX = 0;
    this.dragStartPos = 0;
    this.resumeTimer = null;
    this.nudgeAnimId = null;
    this.lastTime = null;
    this.rafId = null;

    this.measure();
    this._onResize = () => this.measure();
    window.addEventListener('resize', this._onResize);

    this.bindEvents();
    this.loop = this.loop.bind(this);
    this.rafId = requestAnimationFrame(this.loop);
  }

  disconnectedCallback() {
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this.rafId) cancelAnimationFrame(this.rafId);
    if (this.nudgeAnimId) cancelAnimationFrame(this.nudgeAnimId);
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
  }

  measure() {
    this.groupWidth = this.groups[0].getBoundingClientRect().width || 1;
    this.durationMs = window.innerWidth < 750 ? 30000 : 46000;
  }

  wrap(pos) {
    const w = this.groupWidth;
    return ((pos % w) + w) % w;
  }

  render() {
    this.track.style.transform = 'translateX(' + -this.position + 'px)';
  }

  loop(now) {
    if (this.lastTime == null) this.lastTime = now;
    const dt = Math.min(now - this.lastTime, 50);
    this.lastTime = now;

    if (!this.autoPaused && !this.manualPaused && !this.isDragging && !this.nudgeAnimId) {
      const speed = this.groupWidth / this.durationMs;
      this.position = this.wrap(this.position + speed * dt);
      this.render();
    }

    this.rafId = requestAnimationFrame(this.loop);
  }

  pauseAuto() {
    this.autoPaused = true;
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
  }

  scheduleResume(delay) {
    if (this.manualPaused) return;
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(() => {
      this.autoPaused = false;
    }, delay == null ? 2200 : delay);
  }

  bindEvents() {
    this.addEventListener('mouseenter', () => this.pauseAuto());
    this.addEventListener('mouseleave', () => {
      if (!this.isDragging) this.scheduleResume(900);
    });

    this.viewport.style.touchAction = 'pan-y';
    this.viewport.addEventListener('pointerdown', (e) => this.onPointerDown(e));
    this.viewport.addEventListener('pointermove', (e) => this.onPointerMove(e));
    this.viewport.addEventListener('pointerup', (e) => this.onPointerUp(e));
    this.viewport.addEventListener('pointercancel', (e) => this.onPointerUp(e));

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.nudge(-1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nudge(1));
    }

    if (this.playPauseBtn) {
      this.playPauseBtn.addEventListener('click', () => {
        this.manualPaused = !this.manualPaused;
        this.playPauseBtn.setAttribute('aria-pressed', String(this.manualPaused));
        this.playPauseBtn.classList.toggle('is-paused', this.manualPaused);
        if (!this.manualPaused) this.scheduleResume(0);
      });
    }
  }

  onPointerDown(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    this.isDragging = true;
    this.pointerId = e.pointerId;
    this.dragStartX = e.clientX;
    this.dragStartPos = this.position;
    this.pauseAuto();
    this.cancelNudge();
    this.viewport.classList.add('is-dragging');
    try {
      this.viewport.setPointerCapture(e.pointerId);
    } catch (err) {
      /* noop */
    }
  }

  onPointerMove(e) {
    if (!this.isDragging || e.pointerId !== this.pointerId) return;
    const dx = e.clientX - this.dragStartX;
    this.position = this.wrap(this.dragStartPos - dx);
    this.render();
  }

  onPointerUp(e) {
    if (!this.isDragging || e.pointerId !== this.pointerId) return;
    this.isDragging = false;
    this.viewport.classList.remove('is-dragging');
    try {
      this.viewport.releasePointerCapture(e.pointerId);
    } catch (err) {
      /* noop */
    }
    this.scheduleResume(1400);
  }

  cancelNudge() {
    if (this.nudgeAnimId) {
      cancelAnimationFrame(this.nudgeAnimId);
      this.nudgeAnimId = null;
    }
  }

  nudge(direction) {
    this.pauseAuto();
    this.cancelNudge();
    const step = Math.max(this.groupWidth * 0.18, 220);
    const delta = direction * step;
    const startPos = this.position;
    const duration = 420;
    const startTime = performance.now();

    const animate = (now) => {
      const t = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      this.position = this.wrap(startPos + delta * eased);
      this.render();
      if (t < 1) {
        this.nudgeAnimId = requestAnimationFrame(animate);
      } else {
        this.nudgeAnimId = null;
        this.scheduleResume(1600);
      }
    };

    this.nudgeAnimId = requestAnimationFrame(animate);
  }
}

if (!customElements.get('smb-brand-marquee')) {
  customElements.define('smb-brand-marquee', SmbBrandMarquee);
}
